import { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n';
import { getSettings, saveSettings } from '../api';
import { useAuth } from './AuthContext';

export type NumberFormat = 'dk' | 'us';
export type MeasurementSystem = 'metric' | 'imperial_us' | 'imperial_uk';
export type PressureUnit = 'bar' | 'psi' | 'kpa';
export type LengthUnit = 'mm' | 'inch';

export interface Settings {
  language: 'da' | 'en';
  timezone: string;
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  distanceUnit: 'km' | 'miles';
  fuelUnit: 'liters' | 'gallons_us' | 'gallons_uk';
  currency: 'DKK' | 'EUR' | 'GBP' | 'USD';
  itemsPerPage: 10 | 25 | 50 | 100;
  vatRate: number;
  numberFormat: NumberFormat;
  measurementSystem: MeasurementSystem;
  pressureUnit: PressureUnit;
  lengthUnit: LengthUnit;
  inspectionReminderKm: number;
  inspectionName: string;
  firstInspectionAge: number;
  inspectionInterval: number;
  inspectionReminderDays: number;
  defaultUser: string;
  gomore_ical_url: string;
}

export const MEASUREMENT_DEFAULTS: Record<MeasurementSystem, Partial<Settings>> = {
  metric:      { pressureUnit: 'bar',  lengthUnit: 'mm',   distanceUnit: 'km',    fuelUnit: 'liters'      },
  imperial_us: { pressureUnit: 'psi',  lengthUnit: 'inch', distanceUnit: 'miles', fuelUnit: 'gallons_us'  },
  imperial_uk: { pressureUnit: 'psi',  lengthUnit: 'mm',   distanceUnit: 'miles', fuelUnit: 'liters'      },
};

const defaults: Settings = {
  language: 'da',
  // Datoer gemmes i UTC og vises i denne zone. Dansk marked først — UTC som standard viste
  // alle bookingtider 2 timer for tidligt om sommeren i kalenderen og detaljevinduerne.
  timezone: 'Europe/Copenhagen',
  dateFormat: 'DD/MM/YYYY',
  distanceUnit: 'km',
  fuelUnit: 'liters' as const,
  currency: 'DKK',
  itemsPerPage: 25,
  vatRate: 25,
  numberFormat: 'dk',
  measurementSystem: 'metric',
  pressureUnit: 'bar',
  lengthUnit: 'mm',
  inspectionReminderKm: 500,
  inspectionName: 'Syn',
  firstInspectionAge: 4,
  inspectionInterval: 2,
  inspectionReminderDays: 60,
  defaultUser: '',
  gomore_ical_url: '',
};

const NUMBER_FORMAT_CONFIG: Record<NumberFormat, { thousand: string; decimal: string }> = {
  dk: { thousand: '.', decimal: ',' },
  us: { thousand: ',', decimal: '.' },
};

export function formatNumber(value: number | string | undefined | null, fmt: NumberFormat, decimals = 2): string {
  if (value == null || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!isFinite(num)) return String(value);
  const { thousand, decimal } = NUMBER_FORMAT_CONFIG[fmt];
  const [intPart, decPart] = num.toFixed(decimals).split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
  return decimals > 0 ? `${intFormatted}${decimal}${decPart}` : intFormatted;
}

interface SettingsContextValue {
  settings: Settings;
  update: (s: Partial<Settings>) => void;
  loading: boolean;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: defaults,
  update: () => {},
  loading: true,
});

// Indstillinger tilhører den indloggede bruger. Key'et på bruger-id genmonteres
// storen ved login/logout/brugerskift, så den henter på ny — før blev de kun hentet
// ved app-start, og efter login kørte appen på defaults indtil man refreshede.
export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userKey = authLoading ? 'pending' : user ? `u${user.id}` : 'anon';
  return (
    <SettingsStore key={userKey} enabled={!authLoading && !!user}>
      {children}
    </SettingsStore>
  );
}

function SettingsStore({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(defaults);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    // Uden login ville kaldet give 401, og apiFetch sender så til /login
    if (!enabled) return;
    getSettings().then(data => {
      const merged = { ...defaults, ...data } as Settings;
      setSettings(merged);
      if (merged.language) i18n.changeLanguage(merged.language);
    }).catch(() => { /* behold defaults */ }).finally(() => setLoading(false));
  }, [enabled]);

  const update = (partial: Partial<Settings>) => {
    const systemDefaults = partial.measurementSystem
      ? MEASUREMENT_DEFAULTS[partial.measurementSystem]
      : {};
    const next = { ...settings, ...systemDefaults, ...partial };
    setSettings(next);
    saveSettings(partial as Record<string, unknown>);
    if (partial.language) i18n.changeLanguage(partial.language);
  };

  return (
    <SettingsContext.Provider value={{ settings, update, loading }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
